/**
 * The things that make the numbers next to them less trustworthy than they look.
 *
 * Rendered ON the page, always, rather than logged or hidden behind a details
 * toggle. Every item here is a state where the totals still add up and are still
 * subtly wrong — an unstaffed tier whose whole book lands nowhere, a pin that is
 * being ignored, an owner the roster has never heard of. The reference
 * implementation dropped 23 accounts exactly this way and stayed green about it,
 * which is why this is a first-class panel and not a console warning.
 *
 * Renders nothing at all when there is nothing to say, so a clean book is not
 * decorated with an empty "no problems" box.
 */
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@andavo/web-ui';
import type { StaffingDiagnostics } from '../lib/model';

export interface DiagnosticsPanelProps {
  diagnostics: StaffingDiagnostics;
  displayName: (key: string) => string;
  tierLabel: (tierKey: string) => string;
}

export function DiagnosticsPanel({
  diagnostics: d,
  displayName,
  tierLabel,
}: DiagnosticsPanelProps) {
  const vacancies = Object.entries(d.vacanciesByTier).filter(([, n]) => n > 0);

  const items: { key: string; tone: 'warn' | 'info'; text: string }[] = [];

  // FIRST, above every other warning. Everything this person holds has already
  // been dropped from the computed book — their tier is short a staffer and
  // their pinned customers have rejoined the distribution — so every number on
  // the page is a plan for a team that does not exist. Nothing else here is
  // that wrong.
  for (const who of d.removedButHolding) {
    const held = [
      who.seats > 0
        ? `${who.seats} team seat${who.seats === 1 ? '' : 's'}`
        : null,
      who.pins > 0
        ? `${who.pins} pinned customer${who.pins === 1 ? '' : 's'}`
        : null,
    ].filter(Boolean);
    items.push({
      key: `removed-${who.csmId}`,
      tone: 'warn',
      text:
        `${who.displayName} has been REMOVED from the team but still holds ${held.join(' and ')}. ` +
        `A removed person is not in the roster read, so none of it reaches the model: ` +
        `${who.seats > 0 ? 'their tier is being planned one person short, and ' : ''}` +
        `${who.pins > 0 ? 'their pinned customers have rejoined the distribution. ' : ''}` +
        `Clear what they hold on the Team tab, or restore them.`,
    });
  }

  for (const { tierKey, accounts } of d.unstaffedTiers) {
    items.push({
      key: `unstaffed-${tierKey}`,
      tone: 'warn',
      text:
        `${tierLabel(tierKey)} has ${accounts} ${accounts === 1 ? 'customer' : 'customers'} ` +
        `and no staffed slot — those customers are in no one's portfolio, and the per-CSM totals below exclude them.`,
    });
  }

  if (d.untranslatablePins.length > 0) {
    items.push({
      key: 'pins',
      tone: 'warn',
      text:
        `${d.untranslatablePins.length} pin(s) name a CSM who is not in the roster read and are being IGNORED: ` +
        `${d.untranslatablePins.slice(0, 5).join(', ')}${d.untranslatablePins.length > 5 ? '…' : ''}.`,
    });
  }

  if (d.ownersNotOnRoster.length > 0) {
    items.push({
      key: 'owners',
      tone: 'warn',
      text:
        `${d.ownersNotOnRoster.length} current owner(s) on the CRM are not on the staffing roster, so their customers ` +
        `are treated as freely distributable rather than staying put: ` +
        `${d.ownersNotOnRoster.slice(0, 5).map(displayName).join(', ')}${d.ownersNotOnRoster.length > 5 ? '…' : ''}.`,
    });
  }

  for (const [tierKey, n] of vacancies) {
    items.push({
      key: `vacancy-${tierKey}`,
      tone: 'info',
      text: `${tierLabel(tierKey)} has ${n} vacant slot(s) — planned hires. No work is distributed to a vacancy.`,
    });
  }

  if (d.idleRosterMembers.length > 0) {
    items.push({
      key: 'idle',
      tone: 'info',
      text:
        `${d.idleRosterMembers.length} roster member(s) hold no customers in this plan: ` +
        `${d.idleRosterMembers.slice(0, 5).map(displayName).join(', ')}${d.idleRosterMembers.length > 5 ? '…' : ''}.`,
    });
  }

  if (items.length === 0) return null;

  return (
    <Card data-testid="staffing-diagnostics">
      <CardHeader>
        <CardTitle>Read this before quoting these numbers</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col gap-2">
          {items.map((item) => (
            <li className="flex items-start gap-2 text-sm" key={item.key}>
              <Badge variant={item.tone === 'warn' ? 'warning' : 'default'}>
                {item.tone === 'warn' ? 'Affects totals' : 'For context'}
              </Badge>
              <span className="text-content-secondary">{item.text}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
